import React, {useEffect, useContext, useCallback, ReactElement, useState} from "react";

import {FetchResult, MutationResult, QueryResult} from "@apollo/client";
import WaitSpinner from "./wait-spinner";
import FieldTable from "./field-table";
import {ToolbarContext} from "../contexts/toolbar-context";
import {DiaFormRelationships, useDiaForm} from "./dia-form";
import {useUserContext} from '../contexts/user-context';
import {FieldTableOptional} from './FieldTable/FieldTableOptional';
import {NonIdealState} from "./NonIdealState";
import { useToast } from "../contexts/toast";
import {FieldTableColumnProps, IdedType} from "./FieldTable/FieldTableTypes";
import GQLChanges from "../utils/graphql-changes";
import {progressToast} from "../utils/progress-toast";
import { graphqlFindResults } from "../graphql-helpers";
import {useUnsavedChangesGuard} from "./useUnsavedChangesGuard";


interface YupSchema {
    validate: (any, Object) => Promise<unknown>
}

export type DataResult<Type> = {
    data: Type[]
    [key:string]: any
}

//var debug = new Set();
type ListEditorArgs<Type extends IdedType> = {
    query: QueryResult<any>
    className: string,
    saveResult: MutationResult,
    // raw mutation
    mutation:(options:any)=>Promise<FetchResult>,
    // wrapped mutation
    save?:(variables:any)=>Promise<FetchResult>,

    title: string,
    columns: FieldTableColumnProps<Type>[],
    optionalColumns?: FieldTableColumnProps<Type>[],
    newRow: () => Type,
    baseUrl: string,
    newRowLabel: string,
    shownColumns?: string[],
    validationSchema?: YupSchema,
    permission: string,
    relationships?: DiaFormRelationships,
    maxOptionalColumns?: number,
    prepareVariables?:(any)=>any,
    onBeforeSubmit?:(any)=>boolean,
    setShownColumns?:(columns:string[])=>void,
    setOptionalColumns?:(any)=>void,
    getRowProps?:(row:Type)=>any,
    getValues: (query: any) => Type[] // an object that may have Type buried somewhere inside it
}


export function ListEditor<Type extends IdedType>(props: ListEditorArgs<Type>): ReactElement {
    const {loading, error, refetch} = props.query;
    const user = useUserContext();
    const {toast} = useToast();
    const [isOptionalListOpen,setIsOptionalListOpen] = useState<HTMLElement>(null);
    // do save, but do not redirect
    //const rows = props.getValues(props.query);
    const doSubmit = useCallback(({values, initialValues,updateInitialValues,hasCompleted,hasFailed}) => {
        if (props.onBeforeSubmit) {
            if (!props.onBeforeSubmit({values, initialValues})) {
                return;
            }
        }
        const changes = new GQLChanges(props.relationships??{});
        const variables = changes.compare(initialValues, values);

        //let updateVariables = props.getValues(variables);
        let updateVariables = variables;
        if (props.prepareVariables) {
            updateVariables = props.prepareVariables(updateVariables);
        }
        const t = progressToast(toast)('SAVE_LIST');
        t.start('Saving..');
        let promise:undefined|Promise<any> = undefined;
        if(props.save) {
            promise = props.save(updateVariables);
        } else {
            promise = props.mutation({variables:{update:updateVariables}});
        }
        return promise.then((result)=>{
            hasCompleted();
            if((updateVariables as any).create?.length||(updateVariables as any).delete?.length) {
                // graphql merges do not merge with local creation lines, and it's hard to resolve that
                // instead we need to wipe all of the data and re-install it
                // deletes are obviously weird
                updateInitialValues(graphqlFindResults(result));
                refetch();
            }
            t.ok('Saved');
        }).catch(()=> {
            hasFailed();
            t.fail('Save failed');
        })
    }, []);
    const diaForm = useDiaForm({
        inputValues: props.getValues(props.query?.data),
        onSubmit: doSubmit,
        validationSchema: props.validationSchema,
        failure: false,
        blankValues: () => null,
        loaded: !props.query.loading,
        reinitialize: false
    });
    const {isDirty, handleSubmit, status} = diaForm;
    const {confirmation} = useUnsavedChangesGuard({
        isDirty: diaForm.isDirty,
        save: () => diaForm.handleSubmit()
    });
    const toolbarContext = useContext(ToolbarContext);
    useEffect(() => {
        const buttons = [{
            type: 'button',
            icon: 'save',
            tooltip: 'Save',
            disabled: !isDirty,
            callback: handleSubmit,
            label: 'Save'
        }];
        if(props.optionalColumns) {
            buttons.unshift({
                type:"button",
                icon:"columns",
                disabled:false,
                tooltip:"Change columns",
                callback:(evt)=>{
                    setIsOptionalListOpen(evt.currentTarget);
                },
                label:'Columns'
            });
        }


        toolbarContext.setSecondary(
            {
                title: props.title,
                secondary: buttons
            });
    }, [isDirty,!!props.optionalColumns]);
    const setOptionalColumns = useCallback((v) => {
        if (diaForm.isDirty) {
            toast('Please save before changing columns', {id: 'COL_CHANGE', variant: 'error'});
            return;
        }
        if(props.setShownColumns) {
            props.setShownColumns(v);
        }
    }, [diaForm.isDirty, props.setOptionalColumns]);


    if (error) {
        return <NonIdealState title="An error has occurred" severity="error" message={error.message}/>;
    }

    if (status == 'loading') {
        return <WaitSpinner variant="panel"/>;
    }
    return (
        <div className={'list-editor is-scrollable ' + (props.className ? ' ' + props.className : '')}>
            {props.saveResult.loading &&
                <WaitSpinner variant="overlay"/>
            }
            {confirmation}
            {!props.maxOptionalColumns &&
                <FieldTable name=""
                            columns={props.columns}
                            newRow={user.hasPermission(props.permission + '.create') ? props.newRow : null}
                            getRowProps={props.getRowProps as any}
                            form={diaForm}
                            readOnly={!user.hasPermission(props.permission + '.edit')}
                            hasDelete={user.hasPermission(props.permission + '.delete')}
                            newRowLabel={props.newRowLabel}
                />
            }
            {props.maxOptionalColumns&& props.maxOptionalColumns > 0 &&
                <FieldTableOptional name=""
                                    columns={props.columns}
                                    newRow={user.hasPermission(props.permission + '.create') ? props.newRow : null}
                                    getRowProps={props.getRowProps as any}
                                    form={diaForm}
                                    setShownColumns={setOptionalColumns}
                                    maxOptionalColumns={props.maxOptionalColumns}
                                    shownColumns={props.shownColumns}
                                    isOptionalListOpen={isOptionalListOpen}
                                    setIsOptionalListOpen={setIsOptionalListOpen}
                                    optionalColumns={props.optionalColumns}
                                    readOnly={!user.hasPermission(props.permission + '.edit')}
                                    hasDelete={user.hasPermission(props.permission + '.delete')}
                                    newRowLabel={props.newRowLabel}
                />
            }
        </div>
    );
}
